import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'

import { MsalProvider, msalInstance } from './auth/MsalProvider'
import { apiTokenRequest } from './auth/msalConfig'
import { ApiError, isTenantStateDenial } from './api/client'
import { defaultQueryRetry } from './api/queryRetry'
import { router } from './router/router'

import './index.css'
// Bootstrap i18next antes del primer render — Doc 19 §3.1.
import i18n from './i18n/config'
import { bindDocumentLang } from './i18n/documentLang'

// Errores de API que cambian el estado de la sesión, para cualquier query o
// mutation:
//   - 401 (token expirado o revocado): re-autenticación vía redirect en lugar
//     de un error genérico.
//   - `TENANT_READ_ONLY` / `TENANT_SUSPENDED` (OT-0073 §4.4): el estado de la
//     organización cambió con la sesión abierta; se vuelve a pedir `/me` para
//     que el shell pase al estado correcto (banner o pantalla de suspendida).
function handleApiError(error: unknown) {
  if (error instanceof ApiError && error.status === 401) {
    void msalInstance?.acquireTokenRedirect(apiTokenRequest)
  } else if (isTenantStateDenial(error)) {
    void queryClient.invalidateQueries({ queryKey: ['me'] })
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleApiError }),
  mutationCache: new MutationCache({ onError: handleApiError }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: defaultQueryRetry,
      refetchOnWindowFocus: false,
    },
  },
})

// i18n locale change → invalidar las queries que dependen del
// `Accept-Language` para que se re-fetcheen con el nuevo locale. Doc 19
// §"Resolución de metadatos de plantillas": el backend devuelve
// `name`/`description` de plantillas resueltos al locale, así que un
// cambio de idioma debe recargar el catálogo, los detalles, las cards
// de artefactos con `source_template_name`, etc.
//
// Excluimos `['me']` del invalidate: su contenido (preferencias del
// user — `users.locale`, tenant activo, rol) NO depende del
// `Accept-Language`; el refetch sería innecesario y abre una ventana
// para race entre `PATCH /me locale=` del switcher y refetch del
// efecto de sync en `useMe`. El switcher mantiene `me.locale`
// vía PATCH explícito; el cache de `me` se refresca naturalmente
// por staleTime/foco. No hay copy localizado en /me que dependa
// del Accept-Language.
i18n.on('languageChanged', () => {
  void queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] !== 'me',
  })
})

bindDocumentLang(i18n, document.documentElement)

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('No encontré #root en index.html')
}

createRoot(rootElement).render(
  <StrictMode>
    <MsalProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </MsalProvider>
  </StrictMode>,
)
